import { faInfoCircle, faMinus, faPlus, faPowerOff } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonGroup, StyledFlex, Tooltip } from '@tryhackme/thm-ui-components';
import { useCallback, useMemo, useState } from 'react';
import { useAppDispatch } from 'src/app/hooks';
import { type SplitScreenTab, setCurrentTab, setVmPanelOpen } from 'src/app/reducers/split-screen.reducer';
import { ToolTip } from 'src/common/components/tool-tip';
import { useExpiredVmTime, useExtendVmTime } from 'src/common/hooks';
import { TerminateMachineModal } from 'src/features/room/components/active-machine-info/components';
import { ExpiredMachineModal } from 'src/features/room/components/active-machine-info/components/expired-machine-modal';
import { MachineExpiringWrapper } from 'src/features/room/components/machine-expiring';
import { AccessVmCountDown } from '../access-vm-count-down';
import { useBootProgress } from '../boot-progress';
import { WebBasedMachineInfoPopUp } from '../web-based-machine-info-popup';
import { AttackBoxExpiringNotice } from './components/attack-box-expiring-notice';
import { GoPremiumModal } from './components/go-premium-modal';
import { StyleActionButton, StyledButtonTab, StyledScreenNavigation, StyledTimeLeft, StyledTooltip } from './split-screen-navigation.styles';
import type { SplitNavigationProps } from './split-screen-navigation.types';
export const SplitScreenNavigation = ({
  tabs,
  currentTab,
  open = false,
  isFullscreen = false
}: SplitNavigationProps) => {
  const [isOpenTerminateMachineModal, setIsOpenTerminateMachineModal] = useState(false);
  const [openWebMachinePopUp, setOpenWebMachinePopUp] = useState(false);
  const [openExpiredMachineModal, setOpenExpiredMachineModal] = useState<boolean>(false);
  const [openPremiumModal, setOpenPremiumModal] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const {
    formattedTime
  } = useExpiredVmTime(currentTab?.vm?.expires ?? undefined, setOpenExpiredMachineModal);
  const {
    handleExtendVmTime
  } = useExtendVmTime();
  const handleOpenPremiumModal = () => {
    setOpenPremiumModal(true);
  };
  const {
    resetBootTimeCountdown
  } = useBootProgress();
  const handleChangeTab = useCallback((tab: SplitScreenTab) => {
    dispatch(setCurrentTab(tab));
    resetBootTimeCountdown();
  }, [dispatch, resetBootTimeCountdown]);
  const currentTabIndex = useMemo(() => {
    const tabIndex = tabs.findIndex(t => t.id === currentTab?.id);
    return tabIndex === -1 ? 0 : tabIndex;
  }, [tabs, currentTab]);
  return <>
      {currentTab?.vm && <>
          <ExpiredMachineModal open={openExpiredMachineModal} onOpenChange={setOpenExpiredMachineModal} />
          <GoPremiumModal open={openPremiumModal} onOpenChange={setOpenPremiumModal} />
          <MachineExpiringWrapper activeMachine={currentTab.vm} />
        </>}

      <StyledScreenNavigation data-testid="screen-navigation" open={open} data-sentry-element="StyledScreenNavigation" data-sentry-source-file="split-screen-navigation.tsx">
        <StyledFlex justifyContent="flex-start" data-sentry-element="StyledFlex" data-sentry-source-file="split-screen-navigation.tsx">
          {currentTab?.vm && <>
              {!isFullscreen && <AccessVmCountDown currentTabIndex={currentTabIndex} />}

              <ToolTip toolTipText="Extend machine timer">
                <StyleActionButton aria-label="extend-time" type="button" onClick={() => {
              handleExtendVmTime(currentTab.id).catch(() => {});
            }}>
                  <FontAwesomeIcon icon={faPlus} />
                </StyleActionButton>
              </ToolTip>

              <ToolTip toolTipText="Terminate Machine">
                <StyleActionButton aria-label="terminate-machine" type="button" onClick={() => {
              setIsOpenTerminateMachineModal(true);
            }}>
                  <FontAwesomeIcon icon={faPowerOff} />
                </StyleActionButton>
              </ToolTip>
            </>}

          {!isFullscreen && <ToolTip toolTipText="Exit split view">
              <StyleActionButton onClick={() => dispatch(setVmPanelOpen(false))} aria-label="exit-split-view" type="button">
                <FontAwesomeIcon icon={faMinus} />
              </StyleActionButton>
            </ToolTip>}
          {currentTab?.vm && <StyleActionButton aria-label="info" type="button" onClick={() => setOpenWebMachinePopUp(true)}>
              <FontAwesomeIcon icon={faInfoCircle} />
            </StyleActionButton>}

          <div>
            <ButtonGroup isDarkVersion data-sentry-element="ButtonGroup" data-sentry-source-file="split-screen-navigation.tsx">
              {[...tabs].map(tab => <StyledButtonTab key={tab.id} role="tab" onClick={() => handleChangeTab(tab)} aria-selected={tab.id === currentTab?.id}>
                  {tab.title}
                </StyledButtonTab>)}
            </ButtonGroup>
          </div>
        </StyledFlex>

        {currentTab?.vm && <>
            <StyledTimeLeft>
              <p>{formattedTime}</p>
              <StyledTooltip data-tooltip="time-left">
                <Tooltip arrowPosition="bottom-right">
                  <p>Time left until your AttackBox expires</p>
                </Tooltip>
              </StyledTooltip>
            </StyledTimeLeft>
            <WebBasedMachineInfoPopUp open={openWebMachinePopUp} onOpenChange={setOpenWebMachinePopUp} tabData={currentTab} />
            <TerminateMachineModal open={isOpenTerminateMachineModal} onOpenChange={setIsOpenTerminateMachineModal} runningInstance={currentTab?.vm} />
            <AttackBoxExpiringNotice expires={currentTab?.vm?.expires} onOpenPremiumModal={handleOpenPremiumModal} />
          </>}
      </StyledScreenNavigation>
    </>;
};